import React, { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Button, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import InfoText from 'components/shared/info-text.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { requestClinicConnectionTemplate } from 'apollo/templates/clinic-request';

/** Who is asked: a lab (from Find lab) or a clinic (from Add customer; a chain: its parent). */
export type ConnectionTarget = {
    /** The organization the request is sent for (a chain clinic: the clinic itself). */
    organizationId: string;
    /** Who answers: the lab, or the clinic or its chain's parent. */
    name?: string | null;
    kind: 'LAB' | 'CLINIC';
};

type RequestConnectionDialogProps = {
    target?: ConnectionTarget;
    onClose: () => void;
};

const MAX_MESSAGE_LENGTH = 1000;

/**
 * DD-3702: asks a lab (a clinic's admins) or a Denthub Pro clinic (a lab) to work together,
 * with an optional note. The other side approves before anything is connected.
 */
const RequestConnectionDialog: FC<RequestConnectionDialogProps> = ({ target, onClose }) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [message, setMessage] = useState('');
    const keyPrefix = target?.kind === 'LAB' ? 'REQUEST_LAB' : 'REQUEST_CLINIC';

    const close = () => {
        setMessage('');
        onClose();
    };

    const [sendRequest, { loading: isSending }] = useMutation(requestClinicConnectionTemplate, {
        // The cards' "Request sent" and the outgoing list come from the server.
        refetchQueries: ['labDirectory', 'customerDirectory', 'outgoingClinicRequests'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('client-directory:CLINIC_REQUEST_SENT', { name: target?.name }),
            });
            close();
        },
        onError: (error) => showBoundary(error),
    });

    return (
        <DHDialog
            open={Boolean(target)}
            onClose={close}
            fullWidth
            maxWidth="sm"
            title={translate(`client-directory:${keyPrefix}_TITLE`, { name: target?.name })}
            actions={
                <Button
                    variant="contained"
                    disabled={isSending}
                    onClick={() =>
                        target &&
                        sendRequest({
                            variables: {
                                organizationId: target.organizationId,
                                message: message.trim() || undefined,
                            },
                        })
                    }
                    data-test="clinic-requests.send-request"
                >
                    {translate('client-directory:SEND_SUPPLIER_REQUEST')}
                </Button>
            }
        >
            <InfoText text={translate(`client-directory:${keyPrefix}_INFO`, { name: target?.name })} />
            <TextField
                sx={{ mt: 2 }}
                fullWidth
                multiline
                minRows={4}
                label={translate('client-directory:REQUEST_SUPPLIER_MESSAGE')}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH, 'data-test': 'clinic-requests.request-message' }}
            />
        </DHDialog>
    );
};

export default RequestConnectionDialog;
